import { useState, type FormEvent } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Brand } from './OperationsRoom';
import {
  requestPasswordReset, signInWithOAuth, signInWithPassword, signUpWithPassword, updatePassword,
} from '../lib/auth';

type Mode = 'sign-in' | 'sign-up' | 'forgot' | 'reset';

const HEADING: Record<Mode, string> = {
  'sign-in': 'Sign in to Command',
  'sign-up': 'Create your account',
  'forgot': 'Reset your password',
  'reset': 'Choose a new password',
};

/** Ask for a reset link by email alone. The reply never says whether the account exists. */
export function ForgotPasswordForm({ client, redirectTo, onBack }: { client: SupabaseClient; redirectTo: string; onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const reply = await requestPasswordReset(client, email, redirectTo);
    setBusy(false);
    setMessage(reply);
  };

  return (
    <form onSubmit={onSubmit} className="stack">
      <label>Email<input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
      <button type="submit" className="primary" disabled={busy}>Send reset link</button>
      <button type="button" className="link" onClick={onBack}>Back to sign in</button>
      {message && <p role="alert">{message}</p>}
    </form>
  );
}

/** Set a new password for the recovery session the reset email produced. */
export function ResetPasswordForm({ client, onDone }: { client: SupabaseClient; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const problem = await updatePassword(client, password, confirm);
    setBusy(false);
    if (problem) { setMessage(problem); return; }
    setDone(true);
    setMessage('Your password has been updated.');
  };

  return (
    <form onSubmit={onSubmit} className="stack">
      <label>New password<input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
      <label>Confirm new password<input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></label>
      {done
        ? <button type="button" className="primary" onClick={onDone}>Continue</button>
        : <button type="submit" className="primary" disabled={busy}>Set new password</button>}
      {message && <p role="alert">{message}</p>}
    </form>
  );
}

export function SignIn({ client, redirectTo, recovery, onRecovered, notice }: {
  client: SupabaseClient;
  redirectTo: string;
  /** Set when the owner came back from a reset email with a recovery session. */
  recovery?: boolean;
  onRecovered?: () => void;
  /**
   * One line above the form saying why the person is back here - set when they were sent
   * back from a signed-in account that belongs to no organization. It explains, and it is
   * never an error: nothing went wrong, they are simply at the wrong door.
   */
  notice?: string | null;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<Mode>(recovery ? 'reset' : 'sign-in');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const switchTo = (next: Mode) => { setMode(next); setMessage(null); };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const problem = mode === 'sign-in'
      ? await signInWithPassword(client, email, password)
      : await signUpWithPassword(client, email, password, redirectTo);
    setBusy(false);
    setMessage(problem ?? (mode === 'sign-up' ? 'Check your email to confirm the account.' : null));
  };

  const emailPasswordForm = (
    <form onSubmit={onSubmit} className="stack">
      <label>Email<input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
      <label>Password<input type="password" autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'} value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
      <button type="submit" className="primary" disabled={busy}>{mode === 'sign-in' ? 'Sign in' : 'Create account'}</button>
    </form>
  );

  return (
    <main className="centered card auth">
      <Brand />
      <h1>{HEADING[mode]}</h1>
      {notice !== undefined && notice !== null && notice !== '' && mode === 'sign-in' && (
        <p className="muted">{notice}</p>
      )}
      {(mode === 'sign-in' || mode === 'sign-up') && (
        <>
          <button type="button" className="google" disabled={busy} onClick={async () => setMessage(await signInWithOAuth(client, redirectTo))}>
            Continue with Google
          </button>
          <p className="divider"><span>or use email</span></p>
        </>
      )}
      {mode === 'forgot'
        ? <ForgotPasswordForm client={client} redirectTo={redirectTo} onBack={() => switchTo('sign-in')} />
        : mode === 'reset'
          ? <ResetPasswordForm client={client} onDone={() => { if (onRecovered) onRecovered(); }} />
          : emailPasswordForm}
      {mode === 'sign-in' && (
        <button type="button" className="link" onClick={() => switchTo('forgot')}>Forgot password?</button>
      )}
      {(mode === 'sign-in' || mode === 'sign-up') && (
        <button type="button" className="link" onClick={() => switchTo(mode === 'sign-in' ? 'sign-up' : 'sign-in')}>
          {mode === 'sign-in' ? 'Create an account instead' : 'I already have an account'}
        </button>
      )}
      {message && <p role="alert">{message}</p>}
    </main>
  );
}